import {BaseFactory} from "../../../infrastructure/factories/base-factory";
import {PartialGameResponseApiModel} from "../models/partial-game-response-api-model";
import {PartialGameDataSource} from "../models/partial-game-data-source";
import {PartialGameApiModel} from "../models/partial-game-api-model";
import {contractsUtils} from "../../../infrastructure/utils/contracts-utils";
import {PartialGameDomainModel} from "../models/partial-game-domain-model";
import {CompetitionDomainModel} from "../../common/competition/models/competition-domain-model";
import {GameCompetitorDomainModel} from "../models/game-competitor/game-competitor-domain-model";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {SportDomainModel} from "../../sports/models/sport-domain-model";
import {CountryDomainModel} from "../../common/country/models/country-domain-model";
import {GameCompetitorApiModel} from "../models/game-competitor/game-competitor-api-model";
import {LineupsDomainModel} from "../../yard/lineups/lineups-domain-model";
import {PartialGameViewModel} from "../models/partial-game-view-model";
import {LineupsApiModel} from "../../yard/lineups/lineups-api-model";
import {LineupsMemberApiModel} from "../../yard/lineups/lineups-member/lineups-member-api-model";
import {PartialGamesViewModel} from "../models/partial-games.view-model";
import {SportsFactory} from "../../sports/domain-services/sports.factory";
import {CountryFactory} from "../../common/country/domain-services/country-factory";
import {CompetitionFactory} from "../../common/competition/domain-services/competition-factory";
import {EventFactory} from "../../common/events/domain-service/event-factory";
import {COMPETITOR_TYPES} from "../../../core/app/consts";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {OddsDomainModel} from "../../common/odds/models/odds.domain-model";
import {OddsFactory} from "../../common/odds/domain-services/odds.factory";
import {BookmakerApiModel} from "../../common/bookmaker/models/bookmaker-api-model";
import {BookmakerDomainModel} from "../../common/bookmaker/models/bookmaker-domain-model";
import {RankingsDomainModel} from "../../yard/rankings/rankings.domain-model";
import {CompetitorDomainModel} from "../../common/competitor/models/competitor-domain-model";
import {CompetitorFactory} from "../../common/competitor/domain-services/competitor-factory";
import {MemberApiModel} from "../../yard/member/member-api-model";
import {SummaryDomainModel} from "../../full-game/models/summary/summary.domain-model";
import {OddsPreviewDomainModel} from "../../common/odds-preview/models/odds-preview.domain-model";
import {LineupFactory} from "../../yard/lineups/lineup.factory";
import {SportApiModel} from "../../sports/models/sport-api-model";
import type {PromotedPredictionsApiModel} from "../../full-game/models/promoted-predictions/promoted-predictions.api-model";
import {RoundFilterDomainModel} from "../models/round-filter/round-filter.domain-model";
import type {CompetitionApiModel} from "../../common/competition/models/competition-api-model";
import type {BaseApiRequestParams} from "../../../infrastructure/models/params/base-api-request-params";
import type {BaseApiResponseModel} from "../../../infrastructure/models/base-api-response-model";

export class PartialGameFactory extends BaseFactory {
  eventFactory: EventFactory;
  sportFactory: SportsFactory;
  countryFactory: CountryFactory;
  competitionFactory: CompetitionFactory;
  competitorFactory: CompetitorFactory;
  datetimeUtils: DatetimeUtils;
  lineupFactory: LineupFactory;

  static injectables = [...BaseFactory.injectables, EventFactory, SportsFactory, CountryFactory, CompetitionFactory, CompetitorFactory, OddsFactory, DatetimeUtils, LineupFactory];

  constructor(logger: LoggerUtils, terms: TermsUtils, eventFactory: EventFactory, sportFactory: SportsFactory, countryFactory: CountryFactory, competitionFactory: CompetitionFactory, competitorFactory: CompetitorFactory, oddsFactory: OddsFactory, datetimeUtils: DatetimeUtils, lineupFactory: LineupFactory) {
    super(logger, terms);

    contractsUtils.requiredType(eventFactory, EventFactory);
    contractsUtils.requiredType(sportFactory, SportsFactory);
    contractsUtils.requiredType(countryFactory, CountryFactory);
    contractsUtils.requiredType(competitionFactory, CompetitionFactory);
    contractsUtils.requiredType(competitorFactory, CompetitorFactory);
    contractsUtils.requiredType(oddsFactory, OddsFactory);
    contractsUtils.requiredType(lineupFactory, LineupFactory);

    this.eventFactory = eventFactory;
    this.sportFactory = sportFactory;
    this.countryFactory = countryFactory;
    this.competitionFactory = competitionFactory;
    this.competitorFactory = competitorFactory;
    this.oddsFactory = oddsFactory;
    this.datetimeUtils = datetimeUtils;
    this.lineupFactory = lineupFactory;
  }

  createResponse(apiModel, request: BaseApiRequestParams): BaseApiResponseModel {
    return new PartialGameResponseApiModel(apiModel, request);
  }

  createDomainModel(response: PartialGameResponseApiModel): PartialGameDataSource {
    const countriesMap: Map<Number, CountryDomainModel> = this.countryFactory.createDomainMap(response.countries, CountryDomainModel);
    const sportsMap: Map<Number, SportDomainModel> = this.sportFactory.createDomainMap(response.sports, SportDomainModel);
    const competitionsMap: Map<Number, CompetitionDomainModel> = this.competitionFactory.createDomainMap(response.competitions, CompetitionDomainModel, 'id', response.sports, response.countries);
    const competitorsMap: Map<Number, CompetitionDomainModel> = this.competitorFactory.createDomainMap(response.competitors, CompetitorDomainModel, 'id', response.sports, response.countries);

    const bookmakers: Array<BookmakerDomainModel> = response.bookmakers?.map((bookmaker: BookmakerApiModel) => new BookmakerDomainModel(bookmaker));
    const roundFilters: Array<RoundFilterDomainModel> = response.roundFilters?.map(round => new RoundFilterDomainModel(round));
    const competitionFilters: Array<CompetitionApiModel> = Array.from(this.competitionFactory.createDomainMap(response.competitionFilters, CompetitionDomainModel, 'id', response.sports, response.countries)?.values())
    const gamesDomainModels: Array<PartialGameDomainModel> = this.createGamesDomainModels(response, response.games, competitionsMap, sportsMap, countriesMap);
    const suggestedGamesDomainModels: Array<PartialGameDomainModel> = this.createGamesDomainModels(response, response.suggestedGames, competitionsMap, sportsMap, countriesMap);

    let summary = null;

    if (response.summary) {
      const lastGameDate = response.summary.lastGameDate ? this.datetimeUtils.toDateObject(response.summary.lastGameDate) : null;
      const nextGameDate = response.summary.nextGameDate ? this.datetimeUtils.toDateObject(response.summary.nextGameDate) : null;

      if (lastGameDate || nextGameDate) {
        summary = new SummaryDomainModel(response.summary, lastGameDate, nextGameDate)
      }
    }

    return new PartialGameDataSource(response, gamesDomainModels, Array.from(countriesMap.values()), Array.from(competitionsMap.values()), Array.from(competitorsMap.values()), bookmakers, response.paging, summary, suggestedGamesDomainModels, roundFilters, competitionFilters);
  }

  createPartialGameDomainObject(apiModel: PartialGameApiModel, competitionDomainModel: CompetitionDomainModel, countriesMap: Map<Number, CountryDomainModel>, sportDomainModel: SportDomainModel, bookmakersApiModel: Array<BookmakerApiModel>): PartialGameDomainModel {
    try {
      const homeCompetitorCountryDomain: CountryDomainModel = countriesMap.get(apiModel.homeCompetitor?.countryId);
      const awayCompetitorCountryDomain: CountryDomainModel = countriesMap.get(apiModel.awayCompetitor?.countryId);

      const homeCompetitor: GameCompetitorDomainModel = this.createGameCompetitorDomainObject(apiModel.homeCompetitor, homeCompetitorCountryDomain, sportDomainModel, apiModel.members);
      const awayCompetitor: GameCompetitorDomainModel = this.createGameCompetitorDomainObject(apiModel.awayCompetitor, awayCompetitorCountryDomain, sportDomainModel, apiModel.members);

      const startTime: Date = this.datetimeUtils.fromISO(apiModel.startTime);
      const odds: OddsDomainModel = apiModel.odds && this.oddsFactory.createDomainModel(apiModel.odds, bookmakersApiModel);
      const oddsPreview: OddsPreviewDomainModel = apiModel.oddsPreview && new OddsPreviewDomainModel(apiModel.oddsPreview)

      const game: PartialGameDomainModel = new PartialGameDomainModel(apiModel, competitionDomainModel, homeCompetitor, awayCompetitor, startTime, sportDomainModel, odds, null, oddsPreview);

      return game;
    } catch (e) {
      console.warn(`cannot create PartialGameDomainModel for: ${apiModel?.id}`, e)

      return null;
    }
  }

  createGameCompetitorDomainObject(apiModel: GameCompetitorApiModel, country: CountryDomainModel, sport: SportDomainModel, members: Array<MemberApiModel>): GameCompetitorDomainModel {
    if (!apiModel) {
      return undefined;
    }

    const lineups: LineupsDomainModel = this.lineupFactory.createDomainModel(apiModel.lineups, apiModel.type === COMPETITOR_TYPES.nationalTeam, members, sport);
    const rankings: RankingsDomainModel = this.lineupFactory.createRankingsDomainObject(apiModel.rankings);

    return new GameCompetitorDomainModel(apiModel, country, sport, lineups, rankings);
  }

  createDomainMap(games: Array<PartialGameApiModel>, type: Type, key: String, competitions: Map<Number, CompetitionDomainModel>, sports: Map<Number, SportApiModel>, countries: Map<Number, CountryDomainModel>) {
    const gamesMap: Map<Number, PartialGameDomainModel> = new Map();
    games?.forEach((game: PartialGameApiModel, index: Number) => {
      const competition: CompetitionDomainModel = competitions.get(game.competitionId);
      const sport: SportApiModel = sports.get(game.sportId);
      const gameDomain: PartialGameDomainModel = this.createPartialGameDomainObject(game, competition, countries, sport);
      gameDomain.sortIndex = index;
      gamesMap.set(gameDomain.id, gameDomain);
    });

    return gamesMap;
  }

  createVM(model: PartialGameDataSource) {
    if (!model) {
      return null;
    }

    const games: Array<PartialGameViewModel> = model.games.map((partialGameDomainModel: PartialGameDomainModel) => {
      try {
        const date: String = this.datetimeUtils.toShortDate(partialGameDomainModel.startTime);
        const time: String = this.datetimeUtils.toShortTime(partialGameDomainModel.startTime);

        const vm = new PartialGameViewModel(partialGameDomainModel, date, time);

        return vm;
      } catch (e) {
        this.logger.error('view model creation failed for: ' + partialGameDomainModel.id, 'PartialGameFactory', e);

        return null;
      }
    })
      .filter(x => x);
    const vm = new PartialGamesViewModel(model, games);

    return vm;
  }

  updateApiResponseModel(response: PartialGameResponseApiModel, delta: PartialGameResponseApiModel) {
    if (!response || !delta) {
      return;
    }

    this.updateModel(response, delta);
    this.updateApiModelArray(response.competitions, delta.competitions);
    this.updateApiModelArray(response.countries, delta.countries);
    this.updateApiModelArray(response.sports, delta.sports);
    this.updateApiModelArray(response.games, delta.games, (a, b) => this.updatePartialGameApiModel(a, b));
    this.updateApiModelArray(response.suggestedGames, delta.suggestedGames, (a, b) => this.updatePartialGameApiModel(a, b));

    return response;
  }

  updatePartialGameApiModel(response: PartialGameApiModel, delta: PartialGameApiModel) {
    if (!response || !delta) {
      return;
    }
    this.updateModel(response, delta);

    this.updateGameCompetitorApiModel(response.homeCompetitor, delta.homeCompetitor);
    this.updateGameCompetitorApiModel(response.awayCompetitor, delta.awayCompetitor);
    this.updatePromotedPredictionsApiModel(response.promotedPredictions, delta.promotedPredictions);
    this.updateApiModelArray(response.stages, delta.stages);

    response.animation = delta.animation;
    response.justEnded = Boolean(delta.justEnded);

    if (delta.odds) {
      response.odds = delta.odds;
    }

    if (delta.oddsPreview) {
      response.oddsPreview = delta.oddsPreview;
    }
  }

  updatePromotedPredictionsApiModel(response: PromotedPredictionsApiModel, delta: PromotedPredictionsApiModel) {
    this.updateApiModelArray(response?.predictions, delta?.predictions, (a, b) => a = b);
    this.updateApiModelArray(response?.playersBetsPredictions, delta?.playersBetsPredictions, (a, b) => a = b);
  }

  updateGameCompetitorApiModel(response: GameCompetitorApiModel, delta: GameCompetitorApiModel) {
    if (!response || !delta) {
      return;
    }
    this.updateModel(response, delta);

    this.updateApiModelArray(response.recentMatches, delta.recentMatches, (a, b) => a = b);
    // this.updateApiModelArray(response.statistics, delta.statistics);
    this.updateLineupsApiModel(response.lineups, delta.lineups);
    response.inPossession = delta.inPossession;
    response.fouls = delta.fouls;
  }

  updateLineupsApiModel(response: LineupsApiModel, delta: LineupsApiModel) {
    if (!response || !delta) {
      return;
    }
    this.updateModel(response, delta);

    this.updateApiModelArray(response.members, delta.members, (a, b) => this.updateMemberLineupsApiModel(a, b));
  }

  updateMemberLineupsApiModel(response: LineupsMemberApiModel, delta: LineupsMemberApiModel) {
    if (!response || !delta) {
      return;
    }
    this.updateModel(response, delta);

    this.updateModel(response.position, delta.position);
    this.updateModel(response.formation, delta.formation);
    this.updateModel(response.yardFormation, delta.yardFormation);
    this.updateModel(response.substitution, delta.substitution);
    this.updateModel(response.injury, delta.injury);
    this.updateModel(response.suspension, delta.suspension);
    this.updateModel(response.stats, delta.stats);
  }

  createGamesDomainModels(response: PartialGameResponseApiModel, games: Array<PartialGameApiModel>, competitionsMap: Map<Number, CompetitionDomainModel>, sportsMap: Map<Number, SportDomainModel>, countriesMap: Map<Number, CountryDomainModel>): Array<PartialGameDomainModel> {
    const gamesDomainModels = (games || []).map((apiModel: PartialGameApiModel) => {
      try {
        const competition: CompetitionDomainModel = competitionsMap.get(apiModel.competitionId);

        contractsUtils.required(competition);

        const sportDomainModel: SportDomainModel = sportsMap.get(apiModel.sportId);
        const partialGameDomainModel: PartialGameDomainModel = this.createPartialGameDomainObject(apiModel, competition, countriesMap, sportDomainModel, response.bookmakers);

        return partialGameDomainModel;
      } catch (e) {
        this.logger.error('domain model creation failed for: ' + apiModel.id, 'PartialGameFactory', e);

        return null;
      }

    }).filter(x => x);
    return gamesDomainModels
  }

}

